// `__sendStatus`, `__pending`, `__error` are intentional client-side
// namespace markers added by the optimistic sendAdminComment thunk to
// distinguish in-flight UI flags from server-returned comment fields.
/* eslint-disable no-underscore-dangle */
import { createSlice } from "@reduxjs/toolkit";
import {
  getTickets,
  getTicket,
  acknowledgeTicket,
  assignTicket,
  escalateTicket,
  updateTicketStatus,
  updateTicketPriority,
  addComment,
  getAnalyticsOverview,
  getAnalyticsSla,
  getAgents,
  getSlaConfigs,
  updateSlaConfig,
  getGlobalSlaConfigs,
  updateGlobalSlaConfig,
  getBusinessHours,
  updateBusinessHours,
  getHolidays,
  createHoliday,
  updateHoliday,
  deleteHoliday,
  getAutoReplies,
  updateAutoReply,
} from "../action/support.action";

const SUPPORT = "support";

const initialState = {
  tickets: [],
  totalTickets: 0,
  ticketsPage: 0,
  ticketsPerPage: 25,
  selectedTicket: null,
  agents: [],
  slaConfigs: [],
  globalSlaConfigs: [],
  businessHours: null,
  holidays: [],
  autoReplies: [],
  analyticsOverview: null,
  analyticsSla: null,
  filterStatus: "",
  filterPriority: "",
  filterModule: "",
  filterAssignee: "",
  filterCompany: "",
  filterDateFrom: "",
  filterDateTo: "",
  searchQuery: "",
  sortBy: "created_at",
  sortOrder: "desc",
  selectedTicketIds: [],
};

const supportSlice = createSlice({
  name: SUPPORT,
  initialState,
  reducers: {
    clearTickets: (state) => {
      state.tickets = [];
      state.totalTickets = 0;
    },
    setPage: (state, action) => {
      state.ticketsPage = action.payload;
    },
    setPerPage: (state, action) => {
      state.ticketsPerPage = action.payload;
    },
    setFilterStatus: (state, action) => {
      state.filterStatus = action.payload;
    },
    setFilterPriority: (state, action) => {
      state.filterPriority = action.payload;
    },
    setFilterModule: (state, action) => {
      state.filterModule = action.payload;
    },
    setFilterAssignee: (state, action) => {
      state.filterAssignee = action.payload;
    },
    setFilterCompany: (state, action) => {
      state.filterCompany = action.payload;
    },
    setFilterDateRange: (state, action) => {
      state.filterDateFrom = action.payload.from;
      state.filterDateTo = action.payload.to;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
    },
    setSelectedTicketIds: (state, action) => {
      state.selectedTicketIds = action.payload;
    },
    toggleTicketSelection: (state, action) => {
      const id = action.payload;
      if (state.selectedTicketIds.includes(id)) {
        state.selectedTicketIds = state.selectedTicketIds.filter((i) => i !== id);
      } else {
        state.selectedTicketIds.push(id);
      }
    },
    clearFilters: (state) => {
      state.filterStatus = "";
      state.filterPriority = "";
      state.filterModule = "";
      state.filterAssignee = "";
      state.filterCompany = "";
      state.filterDateFrom = "";
      state.filterDateTo = "";
      state.searchQuery = "";
    },
    clearSelectedTicket: (state) => {
      state.selectedTicket = null;
    },

    /**
     * Optimistic agent reply (WhatsApp-style send-status). Pushes a
     * synthetic comment into selectedTicket.comments with
     *   __pending: true, __sendStatus: "sending"
     * so the UI shows a clock immediately. The thunk `sendAdminComment`
     * then calls the API and dispatches markAdminCommentSent /
     * markAdminCommentFailed.
     */
    addPendingAdminComment: (state, action) => {
      const { ticketId, comment } = action.payload;
      if (!state.selectedTicket || state.selectedTicket.id !== ticketId) return;
      const comments = state.selectedTicket.comments || [];
      const idx = comments.findIndex((c) => c.id === comment.id);
      const next = {
        ...comment,
        __pending: true,
        __sendStatus: "sending",
        __error: null,
      };
      if (idx >= 0) comments[idx] = next;
      else comments.push(next);
      state.selectedTicket.comments = comments;
    },

    markAdminCommentSent: (state, action) => {
      const { tempId, serverComment } = action.payload;
      if (!state.selectedTicket) return;
      const comments = state.selectedTicket.comments || [];
      const idx = comments.findIndex((c) => c.id === tempId);
      if (idx < 0) return;
      comments[idx] = {
        ...serverComment,
        __pending: false,
        __sendStatus: "sent",
      };
      state.selectedTicket.comments = comments;
    },

    markAdminCommentFailed: (state, action) => {
      const { tempId, error } = action.payload;
      if (!state.selectedTicket) return;
      const comments = state.selectedTicket.comments || [];
      const idx = comments.findIndex((c) => c.id === tempId);
      if (idx < 0) return;
      comments[idx] = {
        ...comments[idx],
        __pending: true,
        __sendStatus: "failed",
        __error: error || "Send failed",
      };
      state.selectedTicket.comments = comments;
    },
  },
  extraReducers: {
    [getTickets.fulfilled]: (state, action) => {
      state.totalTickets = action.payload.recordsFiltered || action.payload.total || 0;
      state.ticketsPage = action.meta.arg.page;
      state.tickets = action.payload.data || [];
    },
    [getTicket.fulfilled]: (state, action) => {
      const next = action.payload.data || action.payload;
      // Preserve client-side send-status flags across silent re-fetches.
      //   - __pending bubbles are kept appended (mid-flight or failed).
      //   - __sendStatus === "sent" is reapplied to the matching server
      //     comment by id so the tick survives a refresh.
      const prevComments =
        state.selectedTicket && state.selectedTicket.id === next.id
          ? state.selectedTicket.comments || []
          : [];
      const sentFlagById = new Map();
      const stillPending = [];
      prevComments.forEach((c) => {
        if (c.__pending === true) {
          stillPending.push(c);
        } else if (c.__sendStatus === "sent") {
          sentFlagById.set(c.id, c);
        }
      });
      const serverComments = (next.comments || []).map((sc) => {
        const prev = sentFlagById.get(sc.id);
        return prev ? { ...sc, __sendStatus: "sent" } : sc;
      });
      next.comments = stillPending.length
        ? [...serverComments, ...stillPending]
        : serverComments;
      state.selectedTicket = next;
    },
    [assignTicket.fulfilled]: (state, action) => {
      const updated = action.payload.data || action.payload;
      if (state.selectedTicket && state.selectedTicket.id === updated.id) {
        state.selectedTicket = { ...state.selectedTicket, ...updated };
      }
      state.tickets = state.tickets.map((t) =>
        t.id === updated.id ? { ...t, ...updated } : t
      );
    },
    [updateTicketStatus.fulfilled]: (state, action) => {
      const updated = action.payload.data || action.payload;
      if (state.selectedTicket && state.selectedTicket.id === updated.id) {
        state.selectedTicket = { ...state.selectedTicket, ...updated };
      }
      state.tickets = state.tickets.map((t) =>
        t.id === updated.id ? { ...t, ...updated } : t
      );
    },
    [updateTicketPriority.fulfilled]: (state, action) => {
      const updated = action.payload.data || action.payload;
      if (state.selectedTicket && state.selectedTicket.id === updated.id) {
        state.selectedTicket = { ...state.selectedTicket, ...updated };
      }
      state.tickets = state.tickets.map((t) =>
        t.id === updated.id ? { ...t, ...updated } : t
      );
    },
    [addComment.fulfilled]: (state, action) => {
      const comment = action.payload.data || action.payload;
      if (state.selectedTicket) {
        state.selectedTicket.comments = [
          ...(state.selectedTicket.comments || []),
          comment,
        ];
      }
    },
    [getAnalyticsOverview.fulfilled]: (state, action) => {
      state.analyticsOverview = action.payload.data || action.payload;
    },
    [getAnalyticsSla.fulfilled]: (state, action) => {
      state.analyticsSla = action.payload.data || action.payload;
    },
    [getAgents.fulfilled]: (state, action) => {
      state.agents = action.payload.data || action.payload || [];
    },
    [getSlaConfigs.fulfilled]: (state, action) => {
      state.slaConfigs = action.payload.data || action.payload || [];
    },
    [updateSlaConfig.fulfilled]: (state, action) => {
      const updated = action.payload.data || action.payload;
      state.slaConfigs = state.slaConfigs.map((config) =>
        config.priority === updated.priority ? { ...config, ...updated } : config
      );
    },
    [acknowledgeTicket.fulfilled]: (state, action) => {
      const updated = action.payload.data || action.payload;
      if (state.selectedTicket && state.selectedTicket.id === updated.id) {
        state.selectedTicket = { ...state.selectedTicket, ...updated };
      }
      state.tickets = state.tickets.map((t) =>
        t.id === updated.id ? { ...t, ...updated } : t
      );
    },
    [escalateTicket.fulfilled]: (state, action) => {
      const escalation = action.payload.data || action.payload;
      if (state.selectedTicket) {
        state.selectedTicket.escalations = [
          ...(state.selectedTicket.escalations || []),
          escalation,
        ];
      }
    },
    [getGlobalSlaConfigs.fulfilled]: (state, action) => {
      state.globalSlaConfigs = action.payload.data || action.payload || [];
    },
    [updateGlobalSlaConfig.fulfilled]: (state, action) => {
      const updated = action.payload.data || action.payload;
      state.globalSlaConfigs = state.globalSlaConfigs.map((config) =>
        config.key === updated.key ? { ...config, ...updated } : config
      );
    },
    [getBusinessHours.fulfilled]: (state, action) => {
      state.businessHours = action.payload.data || action.payload || null;
    },
    [updateBusinessHours.fulfilled]: (state, action) => {
      state.businessHours = action.payload.data || action.payload || null;
    },
    [getHolidays.fulfilled]: (state, action) => {
      state.holidays = action.payload.data || action.payload || [];
    },
    [createHoliday.fulfilled]: (state, action) => {
      const h = action.payload.data || action.payload;
      if (h) state.holidays = [...state.holidays, h].sort((a, b) => a.start_date.localeCompare(b.start_date));
    },
    [updateHoliday.fulfilled]: (state, action) => {
      const updated = action.payload.data || action.payload;
      state.holidays = state.holidays.map((h) => (h.id === updated.id ? { ...h, ...updated } : h));
    },
    [deleteHoliday.fulfilled]: (state, action) => {
      const id = action.payload?.id || action.meta?.arg?.id;
      state.holidays = state.holidays.filter((h) => h.id !== id);
    },
    [getAutoReplies.fulfilled]: (state, action) => {
      state.autoReplies = action.payload.data || action.payload || [];
    },
    [updateAutoReply.fulfilled]: (state, action) => {
      const updated = action.payload.data || action.payload;
      state.autoReplies = state.autoReplies.map((r) =>
        r.context === updated.context ? { ...r, ...updated } : r
      );
    },
  },
});

export const {
  clearTickets,
  setPage,
  setPerPage,
  setFilterStatus,
  setFilterPriority,
  setFilterModule,
  setFilterAssignee,
  setFilterCompany,
  setFilterDateRange,
  setSearchQuery,
  setSortBy,
  setSelectedTicketIds,
  toggleTicketSelection,
  clearFilters,
  clearSelectedTicket,
  addPendingAdminComment,
  markAdminCommentSent,
  markAdminCommentFailed,
} = supportSlice.actions;

export const supportReducer = supportSlice.reducer;
